import { Injectable } from '@angular/core';
import { RoleType } from 'src/app/nio-common/models';
import { SharedSessionService } from 'src/app/nio-common/services/shared-session.service';
import { AuthContext } from '../models/auth-context.model';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  constructor(private sharedSessionService: SharedSessionService) {}

  /**
   * Get the current user's auth context based on role and organizer status
   */
  getCurrentAuthContext(): AuthContext | null {
    if (!this.sharedSessionService.isLoggedIn) {
      return null;
    }

    const userInfo = this.sharedSessionService.userInfo;
    if (!userInfo) return null;

    // Admin always has ADMIN context
    if (userInfo.role === RoleType.ADMIN) {
      return AuthContext.ADMIN;
    }

    // User with organizerId has ORGANIZER context
    if (userInfo.role === RoleType.USER && userInfo.organizerId) {
      return AuthContext.ORGANIZER;
    }

    // Regular user has USER context
    if (userInfo.role === RoleType.USER) {
      return AuthContext.USER;
    }

    return null;
  }

  /**
   * Check if current user has the specified auth context
   */
  hasAuthContext(requiredContext: AuthContext): boolean {
    const currentContext = this.getCurrentAuthContext();
    return currentContext === requiredContext;
  }

  /**
   * Check if current user has any of the specified auth contexts
   */
  hasAnyAuthContext(requiredContexts: AuthContext[]): boolean {
    const currentContext = this.getCurrentAuthContext();
    return currentContext ? requiredContexts.includes(currentContext) : false;
  }

  /**
   * Check if current user is an admin
   */
  isAdmin(): boolean {
    return this.hasAuthContext(AuthContext.ADMIN);
  }

  /**
   * Check if current user is an organizer
   */
  isOrganizer(): boolean {
    return this.hasAuthContext(AuthContext.ORGANIZER);
  }

  /**
   * Check if current user is a regular user
   */
  isUser(): boolean {
    return this.hasAuthContext(AuthContext.USER);
  }

  /**
   * Check if current user can create events
   */
  canCreateEvent(): boolean {
    return this.isAdmin();
  }

  /**
   * Check if current user can manage events (update, photos, etc.)
   */
  canManageEvents(): boolean {
    return this.hasAnyAuthContext([AuthContext.ADMIN, AuthContext.ORGANIZER]);
  }

  /**
   * Check if current user can manage organizers
   */
  canManageOrganizers(): boolean {
    return this.isAdmin();
  }

  /**
   * Check if current user can manage users
   */
  canManageUsers(): boolean {
    return this.isAdmin();
  }

  /**
   * Check if current user can access admin dashboard
   */
  canAccessAdminDashboard(): boolean {
    return this.hasAnyAuthContext([AuthContext.ADMIN, AuthContext.ORGANIZER]);
  }
}
